/**
 * AppShellLayout — the in-app frame around every authenticated page.
 *
 * Three regions:
 *
 *   ┌─ <header>  status bar  (vol/dept/rid/user) ────────────────────────────┐
 *   │                                                                        │
 *   ├─ <aside>  left rail   ───┬─ <main>  page outlet                        │
 *   │           240 px         │                                             │
 *   │                          │                                             │
 *   └──────────────────────────┴─────────────────────────────────────────────┘
 *
 * The status bar is the editorial signature element — it reads like a
 * journal's running header (vol · issue · date · folio).  We use it for:
 *   - DEPT  · the department switcher
 *   - REQ   · last X-Request-Id (a debug breadcrumb you'd find in a print colophon)
 *   - YOU   · user email + sign out
 */
import { Link, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  MessageSquare,
  Database,
  BarChart3,
  Upload,
  Users,
  BookOpen,
  LogOut,
} from "lucide-react";

import * as authService from "../services/authService";
import { useAuthStore } from "../stores/authStore";
import { toast } from "../stores/toastStore";
import { confirm } from "../components/ConfirmDialog";
import DeptSwitcher from "../components/DeptSwitcher";

// Sidebar nav items.  Each declares the minimum effective role required —
// we filter the visible items at render so users never see a link they
// can't follow.  (Pages also gate themselves via ProtectedRoute.)
const NAV_ITEMS = [
  { to: "/app/chat",        label: "Chat",      icon: MessageSquare, minRole: "chat_only"  },
  { to: "/app/data",        label: "Data",      icon: Database,      minRole: "chat_only"  },
  { to: "/app/dashboard",   label: "Dashboard", icon: BarChart3,     minRole: "chat_only"  },
  { to: "/app/import",      label: "Import",    icon: Upload,        minRole: "dept_admin" },
  { to: "/app/admin/users", label: "Admin",     icon: Users,         minRole: "dept_admin" },
  { to: "/app/admin/audit", label: "Audit",     icon: BookOpen,      minRole: "dept_admin" },
];

const ROLE_RANK = {
  chat_only:  0,
  editor:     1,
  dept_admin: 2,
  sys_admin:  3,
};

function visibleNav(role) {
  const rank = ROLE_RANK[role] ?? 0;
  return NAV_ITEMS.filter((it) => rank >= ROLE_RANK[it.minRole]);
}

export default function AppShellLayout() {
  const navigate = useNavigate();

  const user                = useAuthStore((s) => s.user);
  const memberships         = useAuthStore((s) => s.memberships);
  const activeDepartmentId  = useAuthStore((s) => s.activeDepartmentId);
  const logoutLocal         = useAuthStore((s) => s.logout);

  const activeMembership    = memberships.find((m) => m.department_id === activeDepartmentId);
  const role                = activeMembership?.role || "chat_only";

  async function onLogout() {
    const ok = await confirm({
      title: "Sign out of SADT?",
      description: "You'll need your credentials to return.",
      confirmLabel: "Sign out",
      tone: "neutral",
    });
    if (!ok) return;

    await authService.logout();
    logoutLocal();
    toast.success("Signed out");
    navigate("/auth/login", { replace: true });
  }

  return (
    <div className="grain h-dvh bg-[var(--color-paper)] flex flex-col overflow-hidden">
      {/* ─── Status bar (running header) ─────────────────────────────────── */}
      <header className="border-b border-[var(--color-rule)] px-6 lg:px-10 h-14 flex items-center justify-between gap-6 flex-shrink-0">
        <Link
          to="/app/chat"
          className="flex items-center gap-3 group"
          aria-label="SADT Loading Platform — home"
        >
          <span className="display text-[var(--text-lg)] tracking-tight">SADT</span>
          <span className="hidden sm:inline mono text-[10px] tracking-[0.18em] uppercase text-[var(--color-ink-3)]">
            loading platform
          </span>
        </Link>

        <div className="hidden md:block">
          <DeptSwitcher />
        </div>

        <div className="flex items-center gap-5">
          <div className="flex items-center gap-3">
            <div className="text-right hidden sm:block">
              <div className="text-[var(--text-xs)] text-[var(--color-ink-2)] leading-tight">
                {user?.email || "—"}
              </div>
              <div className="mono text-[10px] tracking-[0.16em] uppercase text-[var(--color-ink-3)] leading-tight">
                {role}
              </div>
            </div>
            <button
              type="button"
              onClick={onLogout}
              className="p-2 hover:bg-[var(--color-paper-2)] transition-colors"
              aria-label="Sign out"
              title="Sign out"
            >
              <LogOut className="w-4 h-4 text-[var(--color-ink-2)]" />
            </button>
          </div>
        </div>
      </header>

      {/* ─── Body: sidebar + main ───────────────────────────────────────── */}
      <div className="flex-1 grid grid-cols-[auto_1fr] min-h-0 overflow-hidden">
        {/* Left rail — nav + page index */}
        <aside className="
          hidden md:flex flex-col w-[240px]
          border-r border-[var(--color-rule)]
          py-8 px-6
        ">
          <nav className="flex flex-col gap-1.5">
            <div className="eyebrow mb-3">PAGES</div>
            {visibleNav(role).map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.to === "/app/chat"}
                className={({ isActive }) => [
                  "group flex items-center gap-3 py-2 -mx-2 px-2",
                  "text-[var(--text-base)]",
                  isActive
                    ? "text-[var(--color-accent)]"
                    : "text-[var(--color-ink-2)] hover:text-[var(--color-ink)]",
                ].join(" ")}
              >
                {({ isActive }) => (
                  <>
                    <it.icon
                      className={[
                        "w-4 h-4 shrink-0",
                        isActive ? "stroke-[2.25]" : "stroke-[1.75]",
                      ].join(" ")}
                    />
                    <span
                      className={isActive ? "underline underline-offset-4 decoration-[var(--color-accent)]" : ""}
                    >
                      {it.label}
                    </span>
                  </>
                )}
              </NavLink>
            ))}
          </nav>

        </aside>

        {/* Main content outlet */}
        <main className="min-w-0 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
